import React, {ReactNode, SyntheticEvent} from 'react';
import {Box, Tab, Tabs, Typography} from '@mui/material';
import './JobResultTabs.scss';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {ReactComponent as OpenInNew} from '../../../../../assets/images/open-in-new.svg';

export type JobResultTabId = 'viewByDocs' | 'viewByIssueCode' | 'docResults';

export interface JobResultTabConfig {
  id: JobResultTabId;
  label: string;
  content: ReactNode;
  disabled?: boolean;
}

export type ViewInDocListAction = {
  enabled: boolean;
  onClick: () => void;
};

export interface JobResultTabsProps {
  value: number;
  onChange: (event: SyntheticEvent, value: number) => void;
  tabs: JobResultTabConfig[];
  isViewInDocListEnabled?: boolean;
  onViewInDocList?: () => void;
}

const a11yProps = (index: number) => ({
  id: `job-result-tab-${index}`,
  'aria-controls': `job-result-tabpanel-${index}`,
});

const JobResultTabs = ({
  value,
  onChange,
  tabs,
  isViewInDocListEnabled = false,
  onViewInDocList,
}: JobResultTabsProps) => {
  const isTabbed = tabs.length > 1;
  const activeTabData = tabs.find((_, index) => index === value) ?? tabs[0];
  const handleContentReverseTab = (event: React.KeyboardEvent) => {
    const isResultsGrid = (event.target as HTMLElement).closest(
      '[role="grid"]'
    );
    if (event.key !== 'Tab' || !event.shiftKey || !isResultsGrid) return;

    event.preventDefault();
    document
      .querySelector<HTMLElement>('[data-testid="doclist-view"]')
      ?.focus({preventScroll: true});
  };

  return (
    <Box className="job-section-container" data-testid="job-result-tabs">
      <Box className="job-header-tabs">
        {isTabbed ? (
          <Tabs value={value} onChange={onChange} className="job-tabs">
            {tabs.map((tab, index) => (
              <Tab
                key={tab.id}
                label={tab.label}
                className="job-tab"
                disabled={tab.disabled}
                data-testid={`job-result-tab-${tab.id}`}
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        ) : (
          <Typography
            className="job-section-title typo-text"
            data-testid="job-result-section-title"
          >
            {activeTabData?.label}
          </Typography>
        )}
        <Box
          className="action-buttons-box"
          data-testid="issue-code-action-button-box"
        >
          <ArrButton
            variant="primary"
            size="medium"
            className="view-doc-list-btn"
            disabled={!isViewInDocListEnabled}
            isAriaDisabled={!isViewInDocListEnabled}
            aria-label={t('ai.review.jobResult.tabs.viewInDocList')}
            dataTestId="doclist-view"
            onClick={() => onViewInDocList?.()}
            endIcon={<OpenInNew />}
            endIconSize="small"
          >
            {t('ai.review.jobResult.tabs.viewInDocList')}
          </ArrButton>
        </Box>
      </Box>
      <Box className="job-content">
        <Box
          className="job-content-panel typo-text body3_size"
          role={isTabbed ? 'tabpanel' : 'region'}
          id={`job-result-tabpanel-${value}`}
          aria-labelledby={
            isTabbed ? `job-result-tab-${value}` : undefined
          }
          aria-label={!isTabbed ? activeTabData?.label : undefined}
          data-testid={`job-result-tabpanel-${activeTabData?.id ?? value}`}
          onKeyDownCapture={handleContentReverseTab}
        >
          {activeTabData?.content}
        </Box>
      </Box>
    </Box>
  );
};

export default JobResultTabs;
